import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FaTwitter, FaLinkedin, FaInstagram } from "react-icons/fa";
import ContactForm from "../ContactForm/ContactFormModal.js";

function Footer() {
    const [showContactForm, setShowContactForm] = useState(false);
    const location = useLocation();
    const isUSAFormationPage = location.pathname === '/usa-company-formation';
    const isTaxReturnPage = location.pathname === '/tax-return';
    const isBookkeepingPage = location.pathname === '/bookkeeping';
    const isCFOPage = location.pathname === '/cfo-advisory';
    const isZohoPage = location.pathname === '/zoho-advisory';

    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
                const response = await fetch(`${apiBase}/api/cms/settings`);
                const result = await response.json();
                if (result.success && result.data) {
                    setSettings(result.data);
                }
            } catch (error) {
                console.error("Failed to load footer settings:", error);
            }
        };
        fetchSettings();
    }, []);

    const footerData = settings?.footer || {};
    const logoData = settings?.logo || {};
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    const footerLogoUrl = logoData.footerLogo
      ? (logoData.footerLogo.startsWith('http') ? logoData.footerLogo : `${apiBase}/${logoData.footerLogo}`)
      : '/logo192.png';
    const logoAlt = logoData.logoAlt || 'BooksComply Logo';
    
    const tagline = footerData.tagline || "Simplifying U.S. bookkeeping, tax filing, and company formation for startups and small businesses worldwide.";
    const contactEmail = footerData.contactEmail || "contact@BooksComply.com";
    const copyrightText = footerData.copyrightText || `NJP Global Services Pvt. Ltd. All Rights Reserved.`;
    
    // Consultation texts
    const fct = footerData.freeConsultationTexts || {};
    const defaultConsultation = fct.default || "Ready to simplify your U.S. bookkeeping and tax compliance?";
    const usaFormationConsultation = fct.usaFormation || "Ready to start your U.S. company? Let BooksComply handle formation, EIN, and compliance — while you focus on growth.";
    const taxReturnConsultation = fct.taxReturn || "Ready to file your U.S. tax return the smart way? Let BooksComply handle your preparation, filing, and compliance — all in one place.";
    const bookkeepingConsultation = fct.bookkeeping || "Ready to simplify your bookkeeping and focus on growing your business? Let BooksComply handle your accounts — from daily entries to year-end reporting.";
    const cfoConsultation = fct.cfo || "Ready to level up your financial strategy? Hire a Virtual CFO in the USA to manage your cash flow, forecasting, and growth planning.";
    const zohoConsultation = fct.zoho || "Ready to set up Zoho Books, Payroll, or Inventory? Get expert implementation, migration, and training designed for U.S. businesses.";

    // Social links
    const socialLinks = footerData.socialLinks || {};
    const linkedinUrl = socialLinks.linkedin || "https://www.linkedin.com/company/njpglobal/";
    const twitterUrl = socialLinks.twitter || "https://x.com/NJP_Advisors/";
    const instagramUrl = socialLinks.instagram || "https://www.instagram.com/bookscomply/";

    const sections = footerData.sections?.length
        ? footerData.sections
        : [
            {
                title: "SERVICES",
                links: [
                    { label: "Bookkeeping", path: "/bookkeeping" },
                    { label: "CFO Advisory", path: "/cfo-advisory" },
                    { label: "Tax Return", path: "/tax-return" },
                    { label: "USA Company Formation", path: "/usa-company-formation" },
                    { label: "Zoho Advisory", path: "/zoho-advisory" }
                ]
            },
            {
                title: "RESOURCES",
                links: [
                    { label: "Blog", path: "/blog" },
                    { label: "Events", path: "/events/zoho-books-ahmedabad" },
                    { label: "Help Center", path: "/help" },
                    { label: "Why BooksComply", path: "/why-bookscomply" }
                ]
            },
            {
                title: "LEGAL",
                links: [
                    { label: "Privacy Policy", path: "/privacy-policy" },
                    { label: "Subscription Agreement", path: "/subscription-agreement" }
                ]
            }
        ];

    const getSectionIconPath = (title) => {
        const t = (title || "").toUpperCase();
        if (t.includes("SERVICE")) {
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />;
        }
        if (t.includes("RESOURCE")) {
            return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />;
        }
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />;
    };

    const handleGetStartedClick = (e) => {
        e.preventDefault();
        setShowContactForm(true);
    };

    const handleCloseContactForm = () => {
        setShowContactForm(false);
    };

    return (
        <div className="relative pt-[120px] bg-white dark:bg-[#020c1b] transition-colors duration-200 font-['Inter','Poppins','Segoe_UI',-apple-system,BlinkMacSystemFont,'Roboto','Helvetica_Neue',sans-serif]">
            {/* Free Consultation Section - Enhanced Design */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 -mb-[120px]">
                <div className="flex justify-center">
                    <div className="w-full lg:w-11/12">
                        <div className="bg-white dark:bg-[#161D30] p-6 md:p-10 rounded-t-3xl border-t-[12px] border-t-orange-500 relative -mt-[100px] mb-[50px] shadow-[0_20px_50px_rgba(0,0,0,0.12)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] backdrop-blur-sm">
                            {/* Decorative gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-[#f8fbff] via-white to-[#e0f2ff] dark:from-[#1e293b] dark:via-[#161D30] dark:to-[#0f172a] rounded-t-3xl opacity-50"></div>

                            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between">
                                <div className="text-center md:text-left mb-6 md:mb-0 md:pr-8">
                                    <h3 className="mb-0 text-[1.15rem] leading-snug font-bold text-[#1a202c] dark:text-white md:text-[1.5rem]">
                                        {isUSAFormationPage
                                            ? usaFormationConsultation
                                            : isTaxReturnPage
                                                ? taxReturnConsultation
                                                : isBookkeepingPage
                                                    ? bookkeepingConsultation
                                                    : isCFOPage
                                                        ? cfoConsultation
                                                        : isZohoPage
                                                            ? zohoConsultation
                                                            : defaultConsultation}
                                    </h3>
                                </div>
                                <div className="flex-shrink-0">
                                    <button
                                        onClick={handleGetStartedClick}
                                        className="group relative font-semibold rounded-full py-4 px-10 bg-gradient-to-r from-[#ff6b00] to-[#e65c00] text-white shadow-[0_8px_25px_rgba(255,107,0,0.35)] whitespace-nowrap transition-all duration-300 hover:shadow-[0_12px_35px_rgba(255,107,0,0.45)] hover:scale-105 overflow-hidden"
                                    >
                                        <span className="relative z-10 flex items-center gap-2">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            Book a free consultation
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#e65c00] to-[#ff6b00] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Footer - Enhanced Design */}
            <footer className="bg-gradient-to-br from-[#020c1b] via-[#061834] to-[#0b2447] text-white pt-[150px] pb-8 relative z-[1] mt-0 animate-fadeIn">

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                        {/* Contact Info with Logo and Description - Enhanced */}
                        <div className="mb-6 text-left">
                            <div className="flex items-center mb-4">
                                <div className="relative group">
                                    <a href="/" className="mr-4 block">
                                        <div className="w-14 h-14 bg-white rounded-2xl p-2 shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:scale-105">
                                            <img
                                                src={footerLogoUrl}
                                                alt={logoAlt}
                                                className="w-full h-full object-contain"
                                                width="40"
                                                height="40"
                                                loading="lazy"
                                            />
                                        </div>
                                    </a>
                                </div>
                                <div>
                                    <h2 className="mb-1 text-2xl font-bold">
                                        <a href="/" className="text-white no-underline hover:text-[#93c5fd] transition-colors duration-300 flex items-center gap-2">
                                            BooksComply
                                            <span className="w-2 h-2 bg-[#93c5fd] rounded-full animate-pulse"></span>
                                        </a>
                                    </h2>
                                    <a href={`mailto:${contactEmail}`} className="text-white/80 hover:text-[#93c5fd] transition-colors duration-300 text-sm flex items-center gap-1 no-underline">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        {contactEmail}
                                    </a>
                                </div>
                            </div>
                            <p className="text-white/70 text-sm mb-0 leading-relaxed max-w-[320px]">
                                {tagline}
                            </p>

                            {/* Contact buttons */}
                            <div className="mt-4 flex flex-col gap-2"></div>
                        </div>

                        {/* Dynamic columns mapping */}
                        {sections.map((section, sIdx) => (
                            <div key={sIdx} className="mb-6 text-left">
                                <div className="flex items-center gap-2 mb-6">
                                    <div className="w-8 h-8 bg-[#93c5fd]/20 rounded-lg flex items-center justify-center">
                                        <svg className="w-5 h-5 text-[#93c5fd]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {getSectionIconPath(section.title)}
                                        </svg>
                                    </div>
                                    <h6 className="font-bold text-lg uppercase tracking-wider">{section.title}</h6>
                                </div>
                                <ul className="list-none p-0 m-0 space-y-3">
                                    {section.links?.map((link, lIdx) => (
                                        <li key={lIdx}>
                                            <a href={link.path} className="text-white/80 hover:text-[#93c5fd] transition-all duration-300 no-underline flex items-center gap-2 group">
                                                <span className="w-1.5 h-1.5 bg-[#93c5fd] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Social Media Icons - Enhanced */}
                    <div className="mt-10">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-white/60 text-sm uppercase tracking-wider">Follow Us</span>
                                <div className="flex gap-3">
                                    <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-[#93c5fd] rounded-full transition-all duration-300 hover:scale-110">
                                        <FaLinkedin className="text-white group-hover:text-[#012f61]" size={18} />
                                    </a>
                                    <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-[#93c5fd] rounded-full transition-all duration-300 hover:scale-110">
                                        <FaTwitter className="text-white group-hover:text-[#012f61]" size={18} />
                                    </a>
                                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-[#93c5fd] rounded-full transition-all duration-300 hover:scale-110">
                                        <FaInstagram className="text-white group-hover:text-[#012f61]" size={18} />
                                    </a>
                                </div>
                            </div>

                            {/* Newsletter Signup */}
                            <div></div>
                        </div>
                    </div>

                    {/* Copyright - Enhanced */}
                    <div className="mt-8 pt-6 border-t border-white/20">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <p className="text-white/60 text-sm mb-0">
                                &copy; {new Date().getFullYear()} {copyrightText}
                            </p>
                            <div className="flex items-center gap-4 text-white/60 text-sm">
                                <span>Powered by</span>
                                <span className="text-[#93c5fd] font-semibold">BooksComply</span>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            {showContactForm && <ContactForm onClose={handleCloseContactForm} />}
        </div>
    );
}

export default Footer;